import React, { MutableRefObject, useRef, useState } from 'react';
import { ManagedFarmFormWrapper } from '../ManagedFarmFormWrapper';
import { managedFarmFormSteps } from '../../../../constants/managedFarm';
import { useNavigate } from 'react-router-dom';
import { managedFarmPageInfo, ownTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import styled from 'styled-components';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { InputValue } from '../../../../interfaces/Element.interfaces';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { managedFarmSlice } from '../../../../store/managedFarmSlice';
import { Helmet } from 'react-helmet';
import { ampli } from '../../../../ampli';

const ERROR_MESSAGES = {
  ownerName: {
    title: '필지 소유자 이름을 입력해주세요',
    buttonName: '소유자 이름 입력하기',
  },
  ownerPhoneNumber: {
    title: '필지 소유자 연락처를 입력해주세요',
    buttonName: '소유자 연락처 입력하기',
  },
  ownerPhoneNumberValidation: {
    title: '',
    buttonName: '',
  },
} as const;

export const ManagedFarmFormFieldOwnerPage = () => {
  const navigate = useNavigate();
  const notification = useNotification();

  const dispatch = useAppDispatch();
  const managedFarmSelector = useAppSelector((state) => state.managedFarm);
  const managedFarmActions = managedFarmSlice.actions;

  const [ownerName, setOwnerName] = useState<string>(managedFarmSelector.fieldOwnerName);
  const [ownerPhoneNumber, setOwnerPhoneNumber] = useState<string>(managedFarmSelector.fieldOwnerPhoneNumber);

  const [inputError, setInputError] = useState<{ title: string; buttonName: string; errorHandler?: () => void }>();
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const fieldOwnerNameInputRef = useRef<HTMLInputElement | null>(null);
  const fieldOwnerPhoneNumberInputRef = useRef<HTMLInputElement | null>(null);

  const currentStep = managedFarmFormSteps.STEP1;
  const steps = Object.values(managedFarmFormSteps);

  const focusInput = (ref: MutableRefObject<HTMLInputElement | null>) => {
    if (ref && ref.current) {
      ref.current.focus();
    }
  };

  const checkHasInputError = (): boolean => {
    if (!ownerName) {
      setInputError({ ...ERROR_MESSAGES.ownerName, errorHandler: () => focusInput(fieldOwnerNameInputRef) });
      setIsShowErrorModal(true);
      return true;
    }

    if (!ownerPhoneNumber) {
      setInputError({
        ...ERROR_MESSAGES.ownerPhoneNumber,
        errorHandler: () => focusInput(fieldOwnerPhoneNumberInputRef),
      });
      setIsShowErrorModal(true);
      return true;
    }

    const phoneNumberRegex = /^010\d{8}$/;
    if (!phoneNumberRegex.test(ownerPhoneNumber)) {
      notification.openNotification({
        message: '전화번호 11자리를 정확히 입력해주세요.',
      });
      focusInput(fieldOwnerPhoneNumberInputRef);
      return true;
    }

    return false;
  };

  const dispatchOwnerInfo = () => {
    if (ownerName && ownerPhoneNumber) {
      dispatch(managedFarmActions.changeFieldOwnerName(ownerName));
      dispatch(managedFarmActions.changeFieldOwnerPhoneNumber(ownerPhoneNumber));
    }
  };

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.formFieldOwnerPage.title}</title>
      </Helmet>
      <ManagedFarmFormWrapper
        headerTitle={'필지 소유자를 입력하세요'}
        canConfirm
        steps={steps}
        currentStep={currentStep}
        confirmHandler={() => {
          const isInputErrorExist = checkHasInputError();
          if (isInputErrorExist) {
            return;
          }
          dispatchOwnerInfo();

          if (managedFarmSelector.address) {
            ampli.pvmGoNextPageManagedFarmFieldOwner({
              field_address: managedFarmSelector.address,
              field_owner_name: ownerName,
              field_owner_phone_number: ownerPhoneNumber,
            });
          }
          navigate(managedFarmPageInfo.formTaskTypePage.path, { replace: true });
        }}
      >
        <SurveyForm>
          <SurveyInputSection>
            <SurveyInputHeader inputTitle={'필지 소유자 이름'} isRequired />
            <InputContainer>
              <BasicInputWithExtras
                type={'string'}
                inputValue={ownerName}
                placeholder={'예) 홍길동'}
                onChange={(value: InputValue) => setOwnerName(value as string)}
                ref={fieldOwnerNameInputRef}
              />
            </InputContainer>
            <SurveyInputHeader inputTitle={'필지 소유자 연락처'} isRequired />
            <InputContainer>
              <BasicInputWithExtras
                type={'string'}
                inputValue={ownerPhoneNumber}
                placeholder={'예) 01000000000'}
                onChange={(value: InputValue) => setOwnerPhoneNumber(value as string)}
                ref={fieldOwnerPhoneNumberInputRef}
              />
            </InputContainer>
          </SurveyInputSection>
        </SurveyForm>
      </ManagedFarmFormWrapper>

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={inputError?.title ?? ''}
        buttonArray={[
          {
            text: inputError?.buttonName ?? '',
            onClick: () => {
              setIsShowErrorModal(false);
              inputError?.errorHandler?.();
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const MapImageContainer = styled.div`
  width: 310px;
  height: 175px;
  border: 1px solid #eee;
`;

const MapImage = styled.img`
  width: 100%;
  height: 100%;
`;
